import { RadioButton, RadioButtonGroup } from "@carbon/react";
import get from "lodash.get";
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  UseControllerProps,
  useFormState,
} from "react-hook-form";

export type RadioBooleanFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  id: string;
  label: string;
  control: Control<TFieldValues>;
  name: TName;
  disabled?: boolean;
  trueLabel?: string;
  falseLabel?: string;
} & Pick<UseControllerProps<TFieldValues, TName>, "rules">;

export function RadioBooleanFormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  id,
  name,
  label,
  control,
  rules,
  disabled,
  trueLabel = "Oui",
  falseLabel = "Non",
}: RadioBooleanFormFieldProps<TFieldValues, TName>) {
  const { errors } = useFormState({ control });
  const error = get(errors, name);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { value, onChange } }) => (
        <RadioButtonGroup
          legendText={label}
          name={id}
          valueSelected={value ?? ""}
          onChange={(val) => onChange(val)}
          invalid={!!error?.message}
          invalidText={typeof error?.message === "string" ? error.message : undefined}
          disabled={disabled}
        >
          <RadioButton id={`${id}-true`} labelText={trueLabel} value="true" />
          <RadioButton id={`${id}-false`} labelText={falseLabel} value="false" />
        </RadioButtonGroup>
      )}
    />
  );
}
